import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { dimensionLabel, unitShort, formatValue, aggregateResults, playerHistoryForDef, DIMENSIONS } from "@/lib/performanceTests";
import { formatDateDE } from "@/lib/playerConstants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Activity, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Minus,
  ChevronRight, ListChecks, History, Trash2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DIM_ACCENT = {
  technik: "text-sky-400 bg-sky-500/10 border-sky-500/30",
  schnelligkeit: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  ausdauer: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  taktik: "text-fuchsia-400 bg-fuchsia-500/10 border-fuchsia-500/30",
};

export default function PerformanceTest() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("overview");
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dimFilter, setDimFilter] = useState("all");
  const [defFilter, setDefFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get("/performance-tests"),
        api.get("/players"),
      ]);
      setTests(tRes.data);
      setPlayers(pRes.data);
    } catch {
      toast.error("Leistungstests konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Available test definitions present in the loaded data, optionally narrowed by dimension filter
  const availableDefs = useMemo(() => {
    const map = new Map();
    for (const t of tests) {
      if (dimFilter !== "all" && t.dimension !== dimFilter) continue;
      if (!map.has(t.test_def_id)) map.set(t.test_def_id, { id: t.test_def_id, name: t.test_name, dimension: t.dimension });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [tests, dimFilter]);

  const filteredTests = useMemo(() => {
    return tests.filter((t) => {
      if (dimFilter !== "all" && t.dimension !== dimFilter) return false;
      if (defFilter !== "all" && t.test_def_id !== defFilter) return false;
      return true;
    });
  }, [tests, dimFilter, defFilter]);

  // Reset defFilter when dimension filter changes and the test def no longer matches
  useEffect(() => {
    if (defFilter === "all") return;
    if (!availableDefs.some((d) => d.id === defFilter)) setDefFilter("all");
  }, [availableDefs, defFilter]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/performance-tests/${confirmDelete.id}`);
      toast.success("Leistungstest gelöscht");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Modul</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Leistungstest</h1>
          <p className="text-zinc-400 text-sm mt-1">
            <span className="text-zinc-100 font-semibold" data-testid="lt-count">{tests.length}</span> Tests durchgeführt
          </p>
        </div>
        <Button
          onClick={() => navigate("/leistungstest/neu")}
          className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
          data-testid="new-lt-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Neuer Leistungstest
        </Button>
      </div>

      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList className="bg-zinc-950 border border-zinc-800 grid grid-cols-2 w-full sm:w-auto rounded-md">
          <TabsTrigger value="overview" className="data-[state=active]:bg-brand data-[state=active]:text-white tracking-wide text-xs" data-testid="lt-view-overview">
            <ListChecks className="h-4 w-4 mr-2" /> Gesamtauswertung
          </TabsTrigger>
          <TabsTrigger value="player" className="data-[state=active]:bg-brand data-[state=active]:text-white tracking-wide text-xs" data-testid="lt-view-player">
            <History className="h-4 w-4 mr-2" /> Einzelspieler-Verlauf
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {loading ? (
            <Loading />
          ) : tests.length === 0 ? (
            <EmptyState onCreate={() => navigate("/leistungstest/neu")} />
          ) : (
            <>
              <FilterBar
                dimFilter={dimFilter}
                setDimFilter={setDimFilter}
                defFilter={defFilter}
                setDefFilter={setDefFilter}
                availableDefs={availableDefs}
                resultCount={filteredTests.length}
                totalCount={tests.length}
              />
              {filteredTests.length === 0 ? (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500" data-testid="lt-filter-empty">
                  Keine Tests mit den aktuellen Filtern.
                </div>
              ) : (
                <div className="space-y-3" data-testid="lt-list">
                  {filteredTests.map((t) => (
                    <TestRow
                      key={t.id}
                      test={t}
                      players={players}
                      onOpen={() => navigate(`/leistungstest/${t.id}`)}
                      onDelete={() => setConfirmDelete(t)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="player" className="mt-4">
          {loading ? (
            <Loading />
          ) : (
            <PlayerHistoryView
              players={players}
              tests={tests}
              selectedPlayerId={selectedPlayerId}
              setSelectedPlayerId={setSelectedPlayerId}
            />
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Leistungstest löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDelete?.test_name} vom {confirmDelete && formatDateDE(confirmDelete.datum)} wird unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700" data-testid="lt-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="lt-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Loading() { return <div className="text-zinc-500 text-sm py-12 text-center">Lädt…</div>; }

function FilterBar({ dimFilter, setDimFilter, defFilter, setDefFilter, availableDefs, resultCount, totalCount }) {
  const dimChips = [{ key: "all", label: "Alle" }, ...DIMENSIONS];
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 sm:p-4 mb-3 space-y-3" data-testid="lt-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Dimension</span>
        {dimChips.map((d) => {
          const active = dimFilter === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => setDimFilter(d.key)}
              className={
                "min-h-[34px] px-3 rounded-full border text-xs font-medium transition " +
                (active
                  ? "bg-brand border-brand text-white"
                  : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60")
              }
              data-testid={`lt-filter-dim-${d.key}`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Test</span>
        <button
          type="button"
          onClick={() => setDefFilter("all")}
          className={
            "min-h-[34px] px-3 rounded-full border text-xs font-medium transition " +
            (defFilter === "all"
              ? "bg-brand border-brand text-white"
              : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60")
          }
          data-testid="lt-filter-def-all"
        >
          Alle
        </button>
        {availableDefs.map((d) => {
          const active = defFilter === d.id;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setDefFilter(d.id)}
              className={
                "min-h-[34px] px-3 rounded-full border text-xs font-medium transition " +
                (active
                  ? "bg-brand border-brand text-white"
                  : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60")
              }
              data-testid={`lt-filter-def-${d.id}`}
            >
              {d.name}
            </button>
          );
        })}
        {availableDefs.length === 0 && (
          <span className="text-[11px] text-zinc-500">Keine Tests in dieser Dimension.</span>
        )}
      </div>
      <div className="text-[11px] text-zinc-500" data-testid="lt-filter-counter">
        {resultCount} von {totalCount} Tests angezeigt
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-10 text-center">
      <Activity className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
      <div className="font-display text-xl font-semibold text-zinc-200 mb-1">Noch keine Leistungstests</div>
      <div className="text-sm text-zinc-500 mb-5">Lege deinen ersten Leistungstest an.</div>
      <Button onClick={onCreate} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md">
        <Plus className="h-4 w-4 mr-2" /> Neuer Leistungstest
      </Button>
    </div>
  );
}

function TestRow({ test, players, onOpen, onDelete }) {
  const agg = useMemo(() => aggregateResults(test, players), [test, players]);
  const accent = DIM_ACCENT[test.dimension] || "text-zinc-400 bg-zinc-800 border-zinc-700";
  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-4"
      data-testid={`lt-row-${test.id}`}
    >
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
          <div className={`h-12 w-12 rounded-md flex flex-col items-center justify-center font-display border ${accent} shrink-0`}>
            <span className="text-[9px] uppercase tracking-wide leading-none mt-1">{dimensionLabel(test.dimension).slice(0, 3)}</span>
            <span className="text-base font-bold leading-none">{test.datum.slice(8, 10)}.{test.datum.slice(5, 7)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-zinc-100 truncate" data-testid={`lt-name-${test.id}`}>{test.test_name}</div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {formatDateDE(test.datum)} · {dimensionLabel(test.dimension)}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">
              {agg.count} Spieler gewertet
              {agg.avg != null && <> · Ø {formatValue(agg.avg, test.unit)}</>}
              {agg.top && <> · Top: <span className="text-brand-lighter">{agg.top.player.name} ({formatValue(agg.top.bestValue, test.unit)})</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="h-8 w-8 inline-flex items-center justify-center rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-800 cursor-pointer"
            data-testid={`lt-delete-${test.id}`}
            role="button"
            aria-label="Test löschen"
          >
            <Trash2 className="h-4 w-4" />
          </span>
          <ChevronRight className="h-5 w-5 text-zinc-600" />
        </div>
      </div>
    </button>
  );
}

function PlayerHistoryView({ players, tests, selectedPlayerId, setSelectedPlayerId }) {
  const selected = players.find((p) => p.id === selectedPlayerId);
  const sortedPlayers = useMemo(() => [...players].sort((a, b) => a.name.localeCompare(b.name)), [players]);

  // Group history by test_def_id for this player
  const histories = useMemo(() => {
    if (!selected) return [];
    const defIds = new Set();
    for (const t of tests) {
      if ((t.results || {})[selected.id]) defIds.add(t.test_def_id);
    }
    return Array.from(defIds).map((defId) => {
      const series = playerHistoryForDef(selected.id, tests, defId);
      const sampleTest = tests.find((t) => t.test_def_id === defId);
      return {
        defId,
        testName: sampleTest?.test_name || "Test",
        unit: sampleTest?.unit || "count",
        direction: sampleTest?.direction || "higher",
        dimension: sampleTest?.dimension || "technik",
        series,
      };
    });
  }, [selected, tests]);

  if (sortedPlayers.length === 0) {
    return <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 text-center text-sm text-zinc-500">Keine Spieler im Kader.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Spieler wählen</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {sortedPlayers.map((p) => {
            const active = p.id === selectedPlayerId;
            const hasData = tests.some((t) => (t.results || {})[p.id]);
            return (
              <button
                key={p.id}
                onClick={() => setSelectedPlayerId(p.id)}
                className={
                  "min-h-[44px] px-3 rounded-md border text-sm font-medium transition text-left " +
                  (active
                    ? "bg-brand border-brand text-white"
                    : hasData
                      ? "bg-zinc-950 border-zinc-700 text-zinc-100 hover:border-brand-lighter/60"
                      : "bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-300")
                }
                data-testid={`lt-player-${p.id}`}
              >
                {p.name}
                {!hasData && <span className="block text-[10px] text-zinc-600">keine Daten</span>}
              </button>
            );
          })}
        </div>
      </div>

      {selected && histories.length === 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 text-center text-sm text-zinc-500">
          Noch keine Tests für {selected.name} erfasst.
        </div>
      )}

      {selected && histories.map((h) => (
        <PlayerTestHistoryCard key={h.defId} history={h} />
      ))}
    </div>
  );
}

function PlayerTestHistoryCard({ history }) {
  const { series, testName, unit, direction, dimension } = history;
  const accent = DIM_ACCENT[dimension] || "text-zinc-400 bg-zinc-800 border-zinc-700";
  const latest = series[series.length - 1];
  const first = series[0];
  const overallDelta = (first && latest && first !== latest && first.bestValue !== 0)
    ? (((latest.bestValue - first.bestValue) / first.bestValue) * 100) * (direction === "lower" ? -1 : 1)
    : null;

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid={`lt-history-${history.defId}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${accent}`}>
            {dimensionLabel(dimension)}
          </span>
          <div className="font-display text-lg font-semibold text-zinc-100 mt-1">{testName}</div>
        </div>
        {overallDelta != null && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Gesamtveränderung</div>
            <div className={"font-display text-xl font-bold tabular-nums flex items-center gap-1 justify-end " +
              (overallDelta > 0 ? "text-emerald-400" : overallDelta < 0 ? "text-rose-400" : "text-zinc-400")}>
              {overallDelta > 0 ? <TrendingUp className="h-4 w-4" /> : overallDelta < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              {overallDelta > 0 ? "+" : ""}{overallDelta.toFixed(1)} %
            </div>
          </div>
        )}
      </div>

      <Sparkline series={series} direction={direction} />

      <div className="mt-4 space-y-1.5">
        {series.map((s, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">{formatDateDE(s.datum)}</span>
            <div className="flex items-center gap-3">
              <span className="font-display font-semibold text-zinc-100 tabular-nums">{formatValue(s.bestValue, unit)}</span>
              {s.deltaPct != null && (
                <span className={
                  "text-xs tabular-nums w-16 text-right " +
                  (s.deltaPct > 0 ? "text-emerald-400" : s.deltaPct < 0 ? "text-rose-400" : "text-zinc-500")
                }>
                  {s.deltaPct > 0 ? "+" : ""}{s.deltaPct.toFixed(1)} %
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sparkline({ series, direction }) {
  if (series.length === 0) return null;
  const values = series.map((s) => s.bestValue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 280;
  const H = 60;
  const PAD = 4;

  const points = series.map((s, i) => {
    const x = series.length === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (series.length - 1);
    // For "lower" direction: higher position = better → flip
    const norm = (s.bestValue - min) / range;
    const y = direction === "lower"
      ? PAD + norm * (H - PAD * 2)
      : PAD + (1 - norm) * (H - PAD * 2);
    return { x, y };
  });

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16">
      <path d={pathD} stroke="#3FA9F5" strokeWidth="2" fill="none" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#3FA9F5" />
      ))}
    </svg>
  );
}
